import {
  AfterViewInit,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { MY_FORMATS } from "../../../../../shared/services/util.service";
import { PageMeta } from "../../../../../shared/models/page.model";
import { CompanyModel } from "../../../../../shared/models/company.model";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";
import { BasePortfolioCreateComponent } from "../../../base-portfolio-create/base-portfolio-create.component";
import { NewPortfolioSearchComponent } from "./new-portfolio-search/new-portfolio-search.component";
import { MaterialModule } from "src/app/app.materials";
import { NewPortfolioActiveSortingComponent } from "./new-portfolio-active-sorting/new-portfolio-active-sorting.component";
import { AssetTableComponent } from "../../../asset-table/asset-table.component";
import { PaginationComponent } from "src/app/components/pagination/pagination.component";
import { SidebarFilterComponent } from "../../../sidebar-filter/sidebar-filter.component";
import { NewPortfolioSelectDialogComponent } from "../new-portfolio-select-dialog/new-portfolio-select-dialog.component";
import { PortfolioSelectionTableComponent } from "../portfolio-selection-table/portfolio-selection-table.component";

@Component({
  selector: "app-new-portfolio",
  templateUrl: "./new-portfolio.component.html",
  styleUrls: ["new-portfolio.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "en-GB" },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  standalone: true,
  imports: [
    NewPortfolioSearchComponent,
    MaterialModule,
    NewPortfolioActiveSortingComponent,
    AssetTableComponent,
    PaginationComponent,
    SidebarFilterComponent,
    NewPortfolioSelectDialogComponent,
    PortfolioSelectionTableComponent,
  ],
})
export class NewPortfolioComponent
  extends BasePortfolioCreateComponent
  implements OnChanges, AfterViewInit
{
  @Input() meta: PageMeta = {};
  @Input() selected: CompanyModel[] = [];
  @Input() priorityCurrencies = [];
  @Input() hideStartDateText = false;
  @Input() drawer = null;
  @Input() portfolio = null;
  @Input() currentTab = 0;
  @Input() portfolioList: any[] = [];
  @Input() selectedPortfolioIds: number[] = [];
  @Input() portfolioMeta: any = {};

  @Output() itemSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectAll: EventEmitter<any> = new EventEmitter<any>();
  @Output() cartClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() fileUploaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() fetchPortfolios: EventEmitter<any> = new EventEmitter<any>();
  @Output() portfolioPageEvent: EventEmitter<any> = new EventEmitter<any>();

  onCheckChange: EventEmitter<any> = new EventEmitter<any>();

  checkedOnly = false;

  previousTab;
  shouldResetTab = false;
  private tabState: Record<
    string,
    { currentFilters: any; sorting: any; checkedOnly: boolean }
  > = {};

  constructor(injector: Injector) {
    super(injector);
    this.storage.filterSelected.subscribe((data) => {
      this.dynamicColumns = data;
    });

    this.onCheckChange.subscribe(() => {
      this.checkedOnly = !this.checkedOnly;
    });
  }

  ngAfterViewInit(): void {
    this.restoreVisibility();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected) {
      const list = changes.selected.currentValue;

      if (list.length) {
        this.checkMaxStartDate(list);
      }
    }
    if (changes.list) {
      this.checkColumnLists(changes.list);
    }
    if (changes.currentTab) {
      this.currentTab = changes.currentTab.currentValue;
    }
  }

  toggle() {
    this.storage.sideMenuObjectChange.emit({
      type: "filters",
      assetType: this.assetType,
      drawer: true,
    });
  }

  onSelect(data) {
    this.itemSelect.emit(data);
  }

  onSelectAllCompany(selected = true) {
    this.selectAll.emit({
      selected,
      type: this.assetType,
    });
  }

  onClickCart() {
    this.cartClick.emit();
  }

  setTab(index: number) {
    this.onTabChange({ index });
  }

  onTabChange(event) {
    this.previousTab = this.currentTab;
    this.saveTabState(this.assetType);
    this.currentTab = event.index;
    switch (this.currentTab) {
      case 0:
        this.shouldResetTab = this.previousTab !== 0;
        this.assetType = "stock";
        this.restoreTabState(this.assetType);
        if (this.shouldResetTab) {
          this.resetTab();
        }
        break;
      case 1:
        this.shouldResetTab = this.previousTab !== 1;
        this.assetType = "fund";
        this.restoreTabState(this.assetType);
        if (this.shouldResetTab) {
          this.resetTab();
        }
        break;
      case 2:
        this.shouldResetTab = this.previousTab !== 2;
        this.assetType = "etf";
        this.restoreTabState(this.assetType);
        if (this.shouldResetTab) {
          this.resetTab();
        }
        break;
      case 3:
        this.shouldResetTab = this.previousTab !== 3;
        this.assetType = "portfolio";
        if (this.shouldResetTab) {
          this.fetchPortfolios.emit();
        }
        break;
      case 4:
        this.onOverview();
        break;
      default:
        this.assetType = "stock";
    }
    this.storage.sideMenuObjectChange.emit({
      type: "filters",
      assetType: this.assetType,
      drawer: false,
    });
  }

  private saveTabState(assetType: string) {
    if (!assetType) {
      return;
    }
    this.tabState[assetType] = {
      currentFilters: this.cloneState(this.currentFilters),
      sorting: this.cloneState(this.sorting),
      checkedOnly: this.checkedOnly,
    };
  }

  private restoreTabState(assetType: string) {
    const state = this.tabState[assetType];
    if (state) {
      this.currentFilters = this.cloneState(state.currentFilters);
      this.sorting = this.cloneState(state.sorting);
      this.checkedOnly = state.checkedOnly;
    } else {
      this.resetFilters();
      this.checkedOnly = false;
    }
    this.updateFilterLabels();
  }

  private cloneState(value: any) {
    return value ? JSON.parse(JSON.stringify(value)) : value;
  }

  resetTab() {
    this.loading = true;
    this.meta = {};
    this.list = [];
    this.restoreVisibility();
    this.onFetchCompanies();
  }

  // Fetch market values at start date
  onOverview(openCartAfterReceived = true) {
    const securities = this.selected.map((s) => s.security_id);
    if (securities.length && this.portfolio && this.portfolio.start_date) {
      this.service
        .getMarketValuesAtStart({
          date: this.portfolio.start_date,
          securities: securities,
        })
        .subscribe((resp: any) => {
          if (resp && resp.success) {
            this.portfolio.start_date_fixed = true;
            const mcapList = resp.success;
            // Mutate in place — never reassign the `selected` @Input, otherwise
            // we detach from the parent's array and stop seeing (de)selections.
            this.selected.forEach((company: CompanyModel) => {
              if (mcapList[company.security_id]) {
                company.market_value_start = `${mcapList[company.security_id].mcap / 1000}`; // Fix mcp value
              }
            });
          }
        });
    }
  }

  getDynamicColumns() {}

  get mergedItems() {
    let returnList = [];
    if (this.checkedOnly) {
      let type = "";
      switch (this.assetType) {
        case "stock":
          type = "SHARE";
          break;
        case "fund":
          type = "FUND";
          break;
        case "etf":
          type = "ETF";
          break;
        default:
          type = "SHARE";
          break;
      }

      this.selected.forEach((element) => {
        element.selected = true;
      });
      returnList = this.selected.filter(
        (asset) => asset.security_type_equity === type,
      );
    } else {
      returnList = this.list;
    }
    return returnList;
  }

  onFetchCompanies() {
    if (this.assetType === "portfolio") {
      this.fetchPortfolios.emit({ searchTerm: this.currentFilters.searchTerm });
      return;
    }
    return super.onFetchCompanies();
  }

  onPortfolioSelect(event: any) {
    this.itemSelect.emit({
      company: event.portfolio,
      isSelected: event.isSelected,
    });
  }

  onPortfolioPageEvent(page: number) {
    this.portfolioPageEvent.emit(page);
  }
}
